/**
 * Shared vocabulary for Document Master templates.
 *
 * Document Master (where templates are authored) and the employee Documents tab
 * (where they are generated) both need to know which [{variable}] names the
 * server fills in by itself. Keeping the lists here stops the two screens from
 * drifting apart - a variable the author sees as auto-filled must not turn into
 * a blank on the generate form.
 *
 * Keys mirror `server/helpers/documentTemplateHelper.js` → getEmployeeVariableMap.
 */

/** Document categories a template can belong to (documentTemplate.doc_for). */
export const DOC_FOR_LABELS = {
  onboarding: "Onboarding Documents",
  experience_letter: "Experience Letter",
  relieving_letter: "Relieving Letter",
  discontinuation_form: "Discontinuation Form",
  promotion_letter: "Promotion Letter",
  appraisal_letter: "Appraisal Letter",
  // Generated automatically (if configured) when a resignation is Accepted -
  // filed on the Documents tab, never emailed.
  non_due_letter: "Non Due Letter",
  // The onboarding learning log. Assigned from the joining date and reviewed by
  // the Reporting Manager, like the milestone forms below.
  wil_form: "What I Learnt Form",
  // Tenure milestone review forms. Named "Review Form", never "Probation" -
  // they apply to whoever the targeting below selects, not only probationers.
  review_3_month: "3 Month Review Form",
  review_6_month: "6 Month Review Form",
  review_1_year: "1 Year Review Form",
  // The new-joiner document pack. Raised automatically when an employee is
  // created; a company opts in simply by keeping a template for the kind.
  appointment_letter: "Appointment Letter",
  id_card: "ID Card",
  personal_data_form: "Personal Data Form",
  pf_registration_sheet: "PF Registration Sheet",
};

/** Order the categories are listed in, in dropdowns and grouped selects. */
export const DOC_FOR_ORDER = [
  "onboarding",
  "appraisal_letter",
  "promotion_letter",
  "experience_letter",
  "relieving_letter",
  "discontinuation_form",
  "non_due_letter",
  "wil_form",
  "review_3_month",
  "review_6_month",
  "review_1_year",
  "appointment_letter",
  "id_card",
  "personal_data_form",
  "pf_registration_sheet",
];

/**
 * Variables from Company Document Master (company-doc-master) - text fields and
 * media (signatures / photos / stamp). Media values render as a full URL, so
 * they are used as <img src="[{hr_sign}]">.
 */
export const COMPANY_DOC_MASTER_VARIABLES = [
  { key: "ceo_name", label: "CEO Name", type: "text" },
  { key: "ceo_email", label: "CEO Email", type: "text" },
  { key: "ceo_sign", label: "CEO Signature (image URL)", type: "media" },
  { key: "ceo_photo", label: "CEO Photo (image URL)", type: "media" },
  { key: "hr_name", label: "HR Name", type: "text" },
  { key: "hr_email", label: "HR Email", type: "text" },
  { key: "hr_sign", label: "HR Signature (image URL)", type: "media" },
  { key: "hr_signature", label: "HR Signature (alias)", type: "media" },
  { key: "hrSign", label: "HR Signature (camelCase alias)", type: "media" },
  { key: "hr_photo", label: "HR Photo (image URL)", type: "media" },
  { key: "director_name", label: "Director Name", type: "text" },
  { key: "director_email", label: "Director Email", type: "text" },
  { key: "director_sign", label: "Director Signature (image URL)", type: "media" },
  { key: "director_photo", label: "Director Photo (image URL)", type: "media" },
  // The COMPANY PAN and the COMPANY letterhead address. The employee's own
  // equivalents are the separate `employee_pan` and `permanent_address`
  // system variables -- named apart here so neither can be picked by mistake.
  { key: "pan_number", label: "Company PAN Number", type: "text" },
  { key: "address", label: "Company Address", type: "text" },
  { key: "place", label: "Place", type: "text" },
  { key: "designated_partner", label: "Designated Partner", type: "text" },
  { key: "stamp_duty", label: "Stamp Duty (image URL)", type: "media" },
  { key: "stampDuty", label: "Stamp Duty (alias)", type: "media" },
];

/**
 * Employee / company / date variables the server resolves at generation time.
 * Anything in a template that is NOT listed here (and is not a Company Document
 * Master variable or a company document field) has to be typed in when the
 * document is generated.
 */
export const ALLOWED_SYSTEM_VARIABLES = [
  "name", "firstName", "lastName", "initialName", "email", "contactNumber",
  "employee_full_name", "employee_first_name", "employee_address",
  "gender", "birthdate", "maritalStatus", "fatherName", "motherName",
  "address", "city", "state", "district", "country", "pinCode",
  "address2", "city2", "state2", "district2", "country2", "pinCode2",
  "doj", "joiningDate", "date_of_joining", "experience", "offeredSalary", "monthly_salary",
  "annualCTC", "annual_ctc", "stipendAmount", "designation_name",
  // The only two component-level salary figures HRMS stores. There is no DA,
  // HRA, allowance, EPF, gratuity, bonus, mediclaim or PT field anywhere in the
  // product, so no chip offers one - a token that can never resolve prints a
  // blank line into a signed letter with no error.
  "basic_salary", "gross_salary",
  // Current payroll figures - appraisal and confirmation letters read these off
  // the employee record rather than the offer.
  "ctc_salary", "current_ctc", "currentCTC",
  "salary_grade", "appraisal_month", "appraisalMonth",
  "confirmation_date", "confirmationDate",
  "reporting_manager_name", "reporting_manager_designation",
  "probation_period", "offer_acceptance_date",
  "bankName", "accountHolderName", "accountNumber", "ifsc", "bankBranch",
  "chequeNumber", "chequeAmount", "companyName", "company_name",
  "date", "currentDate", "current_date", "today",
  "documentGenerateDate", "olReturnDate", "employmentEndDate",
  // Offered by the Role & Tenure chips on the separation letters, and used by
  // the built-in experience letter - omitting it made the save guard reject
  // the very template the palette had just inserted.
  "tenure",
  // Promotion. Supplied by the promotion itself rather than read off the
  // employee record, which by render time only knows the NEW designation.
  "new_designation_name", "previous_designation_name", "promotion_effective_date",
  // camelCase spellings of the same values - accepted because hand-authored and
  // AI-generated letters reach for these constantly, and a rejected save gives
  // no clue which name was meant.
  "employeeName", "employeeFirstName", "employeeCode", "designation",
  "newDesignationName", "previousDesignationName", "promotionEffectiveDate",
  "authorizedPersonName",
  "last_date", "lastDate", "last_working_date", "emp_last_working_date",
  "relieving_date", "relievingDate", "resignation_date", "resignationDate",
  "resignation_accepted_date", "resignationAcceptedDate",
  "reason_type", "reasonType", "reason", "other_reason", "otherReason",
  "notice_period", "noticePeriod", "exit_interview", "exitInterview",
  "emp_code", "employee_code", "department_name", "department", "work_location", "branch_name",
  "qualifications", "experienceYrs", "hobbies", "hiddenTalent",
  "emergencyNumber1", "emergencyNumber2", "emergencyNumber3", "relation1", "relation2", "relation3",
  "employee_signature", "signing_date",
  // Review forms. Supplied by the assignment rather than the employee record,
  // and already used by the shipped templates - `review_date` in particular was
  // in the seeded 3/6/12 month forms while the guard below rejected it, so
  // editing one of those forms and saving it failed on its own wording.
  "review_type", "review_due_date", "review_date",
  // What I Learnt. The onboarding buddy where one is recorded, otherwise the
  // reporting manager.
  "mentor_name", "mentorName",
  // New-joiner document pack. Every one of these is offered as a chip by
  // JOINING_DOCS_VARIABLE_GROUPS in DocumentMaster.js, so omitting them here
  // would make Document Master reject the very template its own palette just
  // built -- the same failure as `tenure`, the promotion variables and
  // `review_date`.
  "spouse_name", "blood_group", "nationality", "religion",
  "personal_email", "personal_mobile", "education_summary",
  // Statutory identifiers. Deliberately NOT `pan_number`: that token is the
  // COMPANY PAN and comes from Company Document Master, so an employee PAN
  // written as `pan_number` would print the company's number on every form.
  "employee_pan", "employee_aadhaar", "uan_number", "pf_number", "esic_number",
  // The employee's own addresses. `address` further up is the COMPANY
  // letterhead address; the renderer flattens these two to one printable line
  // because `employees.address` is Mixed and prints as an object otherwise.
  "permanent_address", "present_address",
  "emergency_contact_name", "emergency_contact_number", "emergency_contact_relation",
  // Inlined as a data URI by the renderer, like the signature images, so the
  // ID card does not wait on an HTTP fetch while the PDF is produced.
  "employee_photo",
];

const COMPANY_DOC_MASTER_KEYS = COMPANY_DOC_MASTER_VARIABLES.map((item) => item.key);

/**
 * Variables in a template that nothing fills in automatically. These are what a
 * user has to type on the Generate Document form - a revised CTC on an
 * appraisal letter, for instance, which lives in no HRMS record.
 *
 * @param {String[]} variables - variables extracted from the template HTML
 * @param {String[]} companyFieldKeys - keys of the company's document fields
 * @returns {String[]} variable names needing manual entry, in template order
 */
export const getManualTemplateVariables = (variables = [], companyFieldKeys = []) => {
  const resolved = new Set([
    ...ALLOWED_SYSTEM_VARIABLES,
    ...COMPANY_DOC_MASTER_KEYS,
    ...companyFieldKeys,
  ]);
  const seen = new Set();
  return (variables || []).filter((variable) => {
    if (!variable || resolved.has(variable) || seen.has(variable)) return false;
    seen.add(variable);
    return true;
  });
};

/** Turns a variable name into a readable form label: revised_ctc -> Revised Ctc. */
export const humanizeVariableName = (variable) =>
  String(variable || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
